import { useMutation } from 'react-query';
import axios, { AxiosError } from 'axios';
import { commonHeaders } from '../../../constants/HeadersAPI';
import { queryClient } from '../../../queryClient';
import { AdminAPI } from '../../../constants/API';
import { useContext } from 'react';
import { defaultSuccessMessage } from '../../../constants/alertText';
import { AlertContext } from '../../../context/AlertProvider';
import { AlertType } from '../../../types/enums';
import { syncEmployeePhoto } from '../../../utils/Admin/employeePhotoApi';
import { ErrorResponseDataType } from '../../../types/Admin/CategoriesTopicsTypes';
import { defaultErrorText } from '../../../types/Error/ErrorResponseType';

export type UserDataBodyType = {
  fullName: string;
  password: string | null;
  role: string;
  branchIds?: number[];
  permissions?: { permissionKey: string; canView: boolean; canEdit: boolean }[];
};

export type UpdateUserDataType = {
  body: UserDataBodyType;
  userId: number;
  queueIds?: number[];
  photo?: string | null;
  initialPhotoUrl?: string | null;
};

export const usePutUserData = (closeModal: (value: boolean) => void) => {
  const context = useContext(AlertContext);
  const {
    mutate: updateUserData,
    isLoading: isUpdateUserDataLoading,
    isError
  } = useMutation(
    'updatedUserData',
    async (data: UpdateUserDataType) => {
      await axios.put(AdminAPI.putUserData(data.userId), data.body, commonHeaders());
      if (data.queueIds != null) {
        await axios.put(AdminAPI.putUserQueues(data.userId), data.queueIds, commonHeaders());
      }
      if (data.photo !== undefined || data.initialPhotoUrl !== undefined) {
        await syncEmployeePhoto(
          data.userId,
          data.photo ?? null,
          data.initialPhotoUrl ?? null
        );
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('allEmployeesData');
        queryClient
          .invalidateQueries('allSystemTopics')
          .then(() =>
            context?.setAlert({ type: AlertType.Success, message: defaultSuccessMessage })
          )
          .then(() => closeModal(false));
      },
      onError: (error: AxiosError<ErrorResponseDataType>): void => {
        if (error instanceof AxiosError) {
          if (400 === error.response?.status) {
            const details = error.response?.data?.details;
            const detailsMessage =
              details?.fullName ?? (details ? Object.values(details)[0] : undefined);
            context?.setAlert({
              type: AlertType.Error,
              message: detailsMessage || error.response?.data?.message
            });
          } else {
            context?.setAlert({ type: AlertType.Error, message: error.response?.data.message });
          }
        } else {
          context?.setAlert({ type: AlertType.Error, message: defaultErrorText });
        }
      }
    }
  );

  return { updateUserData, isUpdateUserDataLoading, isError };
};
